import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

export interface WorkshopOption {
  id: number;
  title: string;
  department_name: string;
}

interface SearchableWorkshopDropdownProps {
  workshops: WorkshopOption[];
  selectedWorkshops: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  placeholder?: string;
  multiple?: boolean;
  className?: string;
  disabled?: boolean;
}

const SearchableWorkshopDropdown: React.FC<SearchableWorkshopDropdownProps> = ({
  workshops,
  selectedWorkshops,
  onSelectionChange,
  placeholder = "Search and select workshops...",
  multiple = true,
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWorkshops, setFilteredWorkshops] = useState<WorkshopOption[]>(workshops);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter workshops based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredWorkshops(workshops);
    } else {
      const filtered = workshops.filter(workshop =>
        workshop.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        workshop.department_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredWorkshops(filtered);
    }
  }, [searchTerm, workshops]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const handleWorkshopSelect = (workshopId: number) => {
    if (multiple) {
      const newSelection = selectedWorkshops.includes(workshopId)
        ? selectedWorkshops.filter(id => id !== workshopId)
        : [...selectedWorkshops, workshopId];
      onSelectionChange(newSelection);
    } else {
      onSelectionChange([workshopId]);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleRemoveWorkshop = (workshopId: number) => {
    const newSelection = selectedWorkshops.filter(id => id !== workshopId);
    onSelectionChange(newSelection);
  };

  const getSelectedWorkshops = () => {
    return workshops.filter(workshop => selectedWorkshops.includes(workshop.id));
  };

  const selectedWorkshopsList = getSelectedWorkshops();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Workshops Display */}
      {selectedWorkshopsList.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedWorkshopsList.map((workshop) => (
            <div
              key={workshop.id}
              className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
            >
              <span className="truncate max-w-[200px]">{workshop.title}</span>
              <button
                type="button"
                onClick={() => handleRemoveWorkshop(workshop.id)}
                className="text-blue-600 hover:text-blue-800"
                disabled={disabled}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropdown Trigger */}
      <div
        onClick={handleToggle}
        className={`
          w-full px-3 py-2 border rounded-md cursor-pointer flex items-center justify-between
          ${disabled 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
            : 'bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
          }
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'}
        `}
      >
        <span className={selectedWorkshopsList.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
          {selectedWorkshopsList.length === 0 
            ? placeholder 
            : `${selectedWorkshopsList.length} workshop${selectedWorkshopsList.length > 1 ? 's' : ''} selected`
          }
        </span>
        <ChevronDown 
          size={16} 
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search workshops..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Workshop List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredWorkshops.length > 0 ? (
              filteredWorkshops.map((workshop) => {
                const isSelected = selectedWorkshops.includes(workshop.id);
                return (
                  <div
                    key={workshop.id}
                    onClick={() => handleWorkshopSelect(workshop.id)}
                    className={`
                      px-3 py-2 cursor-pointer flex items-center space-x-3 hover:bg-gray-50
                      ${isSelected ? 'bg-blue-50' : ''}
                    `}
                  >
                    <input
                      type={multiple ? 'checkbox' : 'radio'}
                      checked={isSelected}
                      onChange={() => {}} // Handled by parent div click
                      className={`
                        ${multiple ? 'rounded border-gray-300' : 'border-gray-300'}
                        text-blue-600 focus:ring-blue-500
                      `}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {workshop.title}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {workshop.department_name}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {searchTerm ? 'No workshops found matching your search.' : 'No workshops available.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableWorkshopDropdown;

